import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import WalletShell from '@/components/wallet/WalletShell';
import WalletActivityRow from '@/components/wallet/WalletActivityRow';
import { mapActivity, cleanBankLabel, type WalletTxnRow } from '@/lib/walletActivity';

// SAB-565 pt2: full transaction history — a simple paginated list reusing the same
// row component + mapping as the wallet-home Recent activity. No per-row detail (v1).
const PAGE = 20;

function HistoryInner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { leagues } = useNFLLeague();
  const [rows, setRows] = useState<WalletTxnRow[]>([]);
  const [bankMap, setBankMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [done, setDone] = useState(false);
  const offset = useRef(0);

  const leagueMap: Record<string, string> = {};
  (leagues || []).forEach((l: any) => { leagueMap[l.id] = l.name; });

  const fetchPage = useCallback(async () => {
    if (!user) return;
    const from = offset.current;
    const { data } = await supabase
      .from('wallet_transactions')
      .select('id, type, amount, status, metadata, description, reference_id, league_id, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .range(from, from + PAGE - 1);
    const batch = (data as any[]) || [];
    setRows((prev) => [...prev, ...batch]);
    offset.current = from + batch.length;
    if (batch.length < PAGE) setDone(true);
  }, [user]);

  useEffect(() => {
    (async () => {
      if (!user) return;
      const { data: b } = await supabase.from('linked_bank_accounts').select('id, bank_name, account_last_four, status').eq('user_id', user.id).eq('status', 'active');
      const map: Record<string, string> = {};
      ((b as any[]) || []).forEach((x) => { map[x.id] = `${cleanBankLabel(x.bank_name)}${x.account_last_four ? ` ••${x.account_last_four}` : ''}`; });
      setBankMap(map);
      await fetchPage();
      setLoading(false);
    })();
  }, [user, fetchPage]);

  const more = async () => { setLoadingMore(true); await fetchPage(); setLoadingMore(false); };

  return (
    <div className="mx-auto w-full max-w-[680px] px-[22px] pt-4 pb-[40px] lg:px-8 lg:pt-9">
        <button onClick={() => navigate('/wallet')} className="flex items-center gap-[5px] text-[15px] font-medium text-muted-foreground mb-2 hover:text-foreground">
          <ChevronLeft className="h-5 w-5" strokeWidth={2.2} /> Wallet
        </button>
        <h1 className="font-display font-extrabold text-[30px] lg:text-[34px] tracking-[-0.02em] text-foreground mb-[22px]">Transaction history</h1>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <div className="rounded-[16px] border border-border bg-card">
            <p className="text-[13.5px] text-muted-foreground text-center px-6 py-10">No activity yet. Your deposits and payouts will show up here.</p>
          </div>
        ) : (
          <>
            <div className="rounded-[16px] border border-border bg-card overflow-hidden divide-y divide-border">
              {rows.map((r) => <WalletActivityRow key={r.id} v={mapActivity(r, bankMap, leagueMap)} />)}
            </div>
            {!done && (
              <button onClick={more} disabled={loadingMore}
                className="mx-auto mt-5 flex items-center gap-2 h-10 px-5 rounded-full border border-border text-[14px] font-semibold text-foreground hover:bg-muted disabled:opacity-60">
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />} Load more
              </button>
            )}
          </>
        )}
      </div>
  );
}

export default function WalletHistory() {
  return (
    <WalletShell>
      <HistoryInner />
    </WalletShell>
  );
}
